import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckIcon from '@mui/icons-material/Check';
import {
  Button,
  Card,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { type ChangePasswordFormData, changePasswordSchema } from './schema';
import { PageContainer } from '@/components/shared/page-container';
import { useNotification } from '@/context/notification';
import { useChangePassword } from '@/data/change-password';
import { useSetTitle } from '@/hooks/use-set-title';

export const ChangePassword = () => {
  useSetTitle('Alterar senha');
  const { toast } = useNotification();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { mutate: changePassword, isPending } = useChangePassword();
  const [successfullyChanged, setSuccessfullyChanged] = useState(false);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (data: ChangePasswordFormData) => {
    changePassword(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          toast({
            content: 'Senha alterada com sucesso!',
            type: 'success',
          });
          setSuccessfullyChanged(true);
        },
        onError: (error) => {
          if (error instanceof AxiosError && error?.status === 401) {
            setError('currentPassword', {
              type: 'manual',
              message: 'Senha atual incorreta.',
            });
          }
          toast({
            content: 'Erro ao alterar senha. Tente novamente.',
            type: 'error',
          });
          setSuccessfullyChanged(false);
        },
      }
    );
  };

  const submitting = isSubmitting || isPending;

  return (
    <PageContainer>
      <Card
        sx={(theme) => ({
          width: { sm: 430 },
          margin: `${theme.spacing(4)} auto`,
          p: 5,
        })}
      >
        <Typography variant="h1" mb={3}>
          Alterar senha
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Controller
              name="currentPassword"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <FormLabel htmlFor="currentPassword">Senha atual</FormLabel>
                  <TextField
                    {...field}
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Digite sua senha atual"
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowCurrentPassword(!showCurrentPassword)
                              }
                              edge="end"
                              tabIndex={-1}
                              aria-label={`${showCurrentPassword ? 'Ocultar' : 'Mostrar'} senha atual`}
                            >
                              {showCurrentPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <FormLabel htmlFor="newPassword">Nova senha</FormLabel>
                  <TextField
                    {...field}
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Digite sua nova senha"
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              edge="end"
                              tabIndex={-1}
                              aria-label={`${showNewPassword ? 'Ocultar' : 'Mostrar'} nova senha`}
                            >
                              {showNewPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth>
                  <FormLabel htmlFor="confirmPassword">
                    Confirmar nova senha
                  </FormLabel>
                  <TextField
                    {...field}
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Digite novamente sua nova senha"
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                              tabIndex={-1}
                              aria-label={`${showConfirmPassword ? 'Ocultar' : 'Mostrar'} confirmar nova senha`}
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </FormControl>
              )}
            />
          </Stack>
          <Stack alignItems="flex-end">
            {successfullyChanged ? (
              <Button
                variant="outlined"
                color="success"
                sx={{ mt: 2 }}
                endIcon={<CheckIcon />}
                onClick={() => navigate('/')}
              >
                Feito
              </Button>
            ) : (
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                sx={{ mt: 2 }}
              >
                {submitting ? <CircularProgress size={16} /> : 'Confirmar'}
              </Button>
            )}
          </Stack>
        </form>
      </Card>
    </PageContainer>
  );
};
